import { useEffect, useState } from 'react';

/**
 * One-time welcome banner shown above the games list for new
 * users. Dismissible — once closed it doesn't come back (the
 * dismissal is keyed by user id in localStorage so a later
 * sign-in as a different user sees their own banner).
 *
 * Empty render when:
 *   - localStorage flag for this user is set, or
 *   - localStorage isn't accessible (private browsing) — fail
 *     closed; better to skip the card than show a confusing one
 *     that won't dismiss.
 */
export function WelcomeCard({ userId }: { userId: string }) {
  const [dismissed, setDismissed] = useState<boolean | null>(null);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(keyFor(userId)) === '1');
    } catch {
      setDismissed(true);
    }
  }, [userId]);

  if (dismissed === null || dismissed) return null;

  function dismiss() {
    try {
      localStorage.setItem(keyFor(userId), '1');
    } catch {
      // Best-effort; the in-memory flag below will hide it for this session.
    }
    setDismissed(true);
  }

  return (
    <div className="mb-4 rounded-lg border border-ec-defence bg-[rgba(85,167,47,0.18)] p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-ec-face">Welcome — first time here?</h3>
          <p className="font-serif text-sm text-ec-bone">
            Click <span className="font-semibold">Play vs AI</span> on any game card to start a
            match against a bot. The board, hand, and controls are the same as a real match — a
            no-stakes way to learn before facing a human opponent.
          </p>
          <p className="font-serif text-xs text-ec-bone-dim">
            Looking for a person to play against? Open the <em>Lobbies</em> tab.
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss welcome message"
          className="ec-btn !py-1 text-xs"
        >
          Got it
        </button>
      </div>
    </div>
  );
}

function keyFor(userId: string): string {
  return `ecg.welcome-dismissed.${userId}`;
}
